import React from "react";
import styled from "@emotion/styled";
import { SidekickTheme } from "@avenue-8/sidekick-ui";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PersonIcon from "@mui/icons-material/Person";
import LockResetIcon from "@mui/icons-material/LockReset";
import SwitchAccountIcon from "@mui/icons-material/SwitchAccount";
import { Button, Menu, MenuItem } from "@mui/material";

const MenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: initial;
  max-width: 2rem;
  padding: 0.5rem 0;
  color: ${(p) => p.theme.text.colors.primary};
  justify-self: center;

  &:hover {
    opacity: 1;
    background-color: ${(p) => p.theme.colors.background3};
  }
`;

const getMenuItemColor = (
  theme: SidekickTheme,
  variant?: "primary" | "danger"
) => {
  const colors = {
    primary: theme.text.colors.primary,
    danger: theme.colors.error,
  };
  return colors[variant ?? "primary"];
};

const CustomMenuItem = styled(MenuItem)<{ variant?: "primary" | "danger" }>`
  color: ${(p) => getMenuItemColor(p.theme, p.variant)};
`;

type Props = {
  onViewProfileClick: () => void;
  onEditUserClick: () => void;
  onUserLogsClick: () => void;
  onUserSubscriptionClick: () => void;
  onResetPasswordClick: () => void;
  onImpersonateClick: () => void;
  isResettingPassword: boolean;
  isImpersonating: boolean;
};

export const UsersListActionsMenu = (props: Props) => {
  const {
    onViewProfileClick,
    onEditUserClick,
    onUserLogsClick,
    onUserSubscriptionClick,
    onResetPasswordClick,
    onImpersonateClick,
    isResettingPassword,
    isImpersonating,
  } = props;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getMenuItemClick =
    (onClick: () => void) => (e: React.MouseEvent<HTMLLIElement>) => {
      e.stopPropagation();
      e.preventDefault();
      onClick();
      handleClose();
    };

  return (
    <div>
      <MenuButton
        id="users-list-actions-button"
        aria-controls={open ? "users-list-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="text"
      >
        <MoreHorizIcon />
      </MenuButton>
      <Menu
        id="users-list-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        autoFocus={false}
        MenuListProps={{
          "aria-labelledby": "users-list-actions-button",
        }}
      >
        <CustomMenuItem onClick={getMenuItemClick(onViewProfileClick)}>
          <PersonIcon fontSize="small" /> View Profile
        </CustomMenuItem>
        <CustomMenuItem onClick={getMenuItemClick(onEditUserClick)}>
          <EditIcon fontSize="small" /> Edit
        </CustomMenuItem>
        <CustomMenuItem onClick={getMenuItemClick(onUserSubscriptionClick)}>
          <CreditCardIcon fontSize="small" /> Subscription
        </CustomMenuItem>
        <CustomMenuItem onClick={getMenuItemClick(onUserLogsClick)}>
          <HistoryIcon fontSize="small" /> Logs
        </CustomMenuItem>
        <CustomMenuItem
          disabled={isResettingPassword}
          onClick={getMenuItemClick(onResetPasswordClick)}
        >
          <LockResetIcon fontSize="small" /> Reset password
        </CustomMenuItem>
        <CustomMenuItem
          disabled={isImpersonating}
          onClick={getMenuItemClick(onImpersonateClick)}
        >
          <SwitchAccountIcon fontSize="small" /> Impersonate
        </CustomMenuItem>
      </Menu>
    </div>
  );
};
